import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import {
  ChevronDown,
  ChevronUp,
  ICON_STROKE,
  Target,
} from "@/shared/components/icons";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import { buttonClass } from "@/shared/components/Button";
import { fetchLeakReport } from "@/shared/api";
import { drillLeakRoute } from "@/shared/chatRoute";
import type { LeakPattern } from "@/shared/models";

// How many new signals trigger an automatic regeneration server-side.
// Mirrors `backend/app/leak_detector.py:REGEN_AFTER_N_NEW_SIGNALS`.
// Used only for UI display — the backend is the source of truth.
const REGEN_AFTER_N_NEW_SIGNALS = 10;

export function LeakReportCard() {
  const { data: report, isLoading } = useQuery({
    queryKey: ["leak-report"],
    queryFn: fetchLeakReport,
  });

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-surface p-4 text-sm text-text-muted">
        Analyzing your drill history…
      </div>
    );
  }
  if (!report) return null;

  if (report.insufficientSignals) {
    return (
      <section className="rounded-xl border border-border bg-surface p-5">
        <h2 className="font-semibold text-text mb-1">Building your leak profile</h2>
        <p className="text-sm text-text-muted">{report.recommendedFocus}</p>
      </section>
    );
  }

  // "X / 10 signals till next report" — X is signals accumulated SINCE the
  // last report (capped at 10 so we never display >10/10).
  const progress = Math.min(
    REGEN_AFTER_N_NEW_SIGNALS,
    REGEN_AFTER_N_NEW_SIGNALS - report.signalsUntilNextReport,
  );
  const progressPct = (progress / REGEN_AFTER_N_NEW_SIGNALS) * 100;

  return (
    <section className="space-y-3">
      <div className="rounded-xl border border-border bg-surface p-5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-text flex items-center gap-2 tracking-tight">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-accent/15 text-accent">
              <Target size={15} strokeWidth={ICON_STROKE} />
            </span>
            <span>Leak Report</span>
          </h2>
        </div>
        <p className="text-sm text-text leading-relaxed">{report.recommendedFocus}</p>

        <div className="mt-4 space-y-1.5">
          <div className="flex items-baseline justify-between text-[11px]">
            <span className="text-text-muted">
              <span className="font-mono tabular-nums text-text">
                {progress} / {REGEN_AFTER_N_NEW_SIGNALS}
              </span>{" "}
              signals till next report
            </span>
            <span className="text-text-muted/70">
              Generated {new Date(report.generatedAt).toLocaleDateString()}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-border/40 overflow-hidden">
            <div
              className="h-full bg-accent/70 rounded-full transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {report.patterns.length === 0 ? (
        <div className="text-sm text-text-muted text-center">
          No clear patterns yet: keep drilling.
        </div>
      ) : (
        report.patterns.map((p) => <PatternCard key={p.id} pattern={p} />)
      )}
    </section>
  );
}

function PatternCard({ pattern }: { pattern: LeakPattern }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article className="rounded-xl border border-border bg-surface overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-4 flex items-start gap-3"
      >
        <SeverityBadge severity={pattern.severity} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-text">{pattern.title}</div>
          <div
            className={["text-xs text-text-muted mt-1", expanded ? "" : "line-clamp-2"].join(" ")}
          >
            {pattern.description}
          </div>
        </div>
        {expanded ? (
          <ChevronUp size={16} strokeWidth={ICON_STROKE} className="text-text-muted/70" />
        ) : (
          <ChevronDown size={16} strokeWidth={ICON_STROKE} className="text-text-muted/70" />
        )}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/50 pt-3">
          {pattern.evidence.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">
                Evidence
              </div>
              <ul className="space-y-1 text-xs text-text">
                {pattern.evidence.map((e, idx) => (
                  <li
                    key={idx}
                    className="pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-text-muted"
                  >
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {pattern.recommendedAction && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Fix</div>
              <p className="text-xs text-text">{pattern.recommendedAction}</p>
            </div>
          )}
          <DrillLaunchLink
            to={drillLeakRoute(pattern)}
            className={buttonClass({ variant: "primary", fullWidth: true, className: "mt-2" })}
          >
            <Target size={14} strokeWidth={ICON_STROKE} />
            Drill this leak
          </DrillLaunchLink>
        </div>
      )}
    </article>
  );
}

function SeverityBadge({ severity }: { severity: LeakPattern["severity"] }) {
  const cls = {
    high: "bg-red-500/80",
    medium: "bg-orange-400/80",
    low: "bg-yellow-400/80",
  }[severity];
  return (
    <span
      className={`text-white text-[10px] tracking-wider font-bold uppercase px-1.5 py-0.5 rounded ${cls} flex-shrink-0`}
    >
      {severity}
    </span>
  );
}
